import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Paper,
  Rating,
  Avatar,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Build,
  Plumbing,
  ElectricalServices,
  AcUnit,
  CleaningServices,
  PestControl,
  FormatPaint,
  Security,
  ThumbUp,
  Schedule,
  Restaurant,
  Verified,
  Speed,
  EmojiEmotions,
} from '@mui/icons-material';

// Theme color variations - all from the same family
const themeColors = {
  primary: '#9b4d3c',      // Dark brown/red
  primaryLight: '#b86b5a',  // Medium brown/red
  secondary: '#d49b7b',     // Light brown/tan
  secondaryLight: '#e5b8a0', // Very light brown/tan
  accent1: '#8a3f2f',       // Darker shade
  accent2: '#c47a5a',       // Medium shade
  accent3: '#dfb195',       // Light shade
  accent4: '#f0d5c5',       // Very light shade
  accent5: '#7a3b2e',       // Darkest shade
  accent6: '#eac7b5',       // Lightest shade
};

// Updated services list with theme color variations
const services = [
  { icon: <Plumbing />, name: 'Plumbing', description: 'Leaks, pipe repairs, installations', rating: 4.8, color: themeColors.primary },
  { icon: <ElectricalServices />, name: 'Electrical', description: 'Wiring, fixtures, troubleshooting', rating: 4.9, color: themeColors.primaryLight },
  { icon: <AcUnit />, name: 'HVAC', description: 'Heating, cooling, ventilation', rating: 4.7, color: themeColors.accent1 },
  { icon: <CleaningServices />, name: 'Deep Cleaning', description: 'Professional cleaning services', rating: 4.8, color: themeColors.secondary },
  { icon: <FormatPaint />, name: 'Interior Work', description: 'Interior design & painting', rating: 4.6, color: themeColors.accent2 },
  { icon: <PestControl />, name: 'Pest Control', description: 'Extermination & prevention', rating: 4.7, color: themeColors.accent5 },
  { icon: <Security />, name: 'Security', description: 'CCTV installation ', rating: 4.9, color: themeColors.secondaryLight },
  { icon: <Build />, name: 'General Repairs', description: 'Handyman services', rating: 4.5, color: themeColors.accent3 },
  { icon: <Restaurant />, name: 'Cook', description: 'Professional cooking services', rating: 4.8, color: themeColors.accent4 },
];

// Section border style - matching homepage and other pages
const sectionBorderStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '4px',
  background: 'linear-gradient(90deg, #9b4d3c 0%, #d49b7b 50%, #e0e0e0 100%)',
  zIndex: 1,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

// Title style with moving underline
const titleWithUnderlineStyle = {
  fontWeight: 600,
  color: '#333',
  fontSize: { xs: '2rem', md: '2.5rem' },
  position: 'relative',
  display: 'inline-block',
  pb: 2,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
    borderRadius: 2,
    animation: 'pulseWidth 2s ease-in-out infinite',
  }
};

const ServicesPage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <Box sx={{ bgcolor: '#faf7f2', minHeight: '100vh' }}>
      {/* SECTION 1: HEADER - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#9b4d3c', 
                  fontSize: '0.9rem', 
                  letterSpacing: 4,
                  fontWeight: 500,
                  display: 'block',
                  mb: 1,
                }}
              >
                PROFESSIONAL SERVICES
              </Typography>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={titleWithUnderlineStyle}
              >
                Room Services
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto',
                  mt: 4,
                }}
              >
                Professional services for your rooms - from repairs to daily needs
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* SECTION 2: AVAILABLE SERVICES HEADER - CENTERED WITH MOVING UNDERLINE */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography 
                variant="h2" 
                component="h2" 
                sx={titleWithUnderlineStyle}
              >
                Available Services
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* SECTION 3: SERVICES GRID - NO BORDER AFTER HEADER */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper 
          sx={{ 
            p: 4,
            bgcolor: 'white',
            borderRadius: 3,
            boxShadow: '0 5px 20px rgba(155,77,60,0.05)',
            border: '2px solid #d49b7b',
          }}
        >
          <Grid container spacing={3}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Fade in={true} timeout={1000 + index * 100}>
                  <Card
                    onMouseEnter={() => setHoveredCard(index)}
                    onMouseLeave={() => setHoveredCard(null)}
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
                      borderRadius: 3,
                      border: '2px solid #9b4d3c',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 15px 30px rgba(155,77,60,0.2)',
                        borderColor: '#d49b7b',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Avatar 
                          sx={{ 
                            background: `linear-gradient(135deg, ${service.color} 0%, ${themeColors.secondary} 100%)`,
                            width: 70,
                            height: 70,
                            transition: 'all 0.3s ease',
                            boxShadow: hoveredCard === index ? '0 8px 16px rgba(155,77,60,0.3)' : 'none',
                            '&:hover': {
                              transform: 'scale(1.1) rotate(5deg)',
                            },
                          }}
                        >
                          <Box sx={{ color: 'white', fontSize: '2rem' }}>
                            {service.icon}
                          </Box>
                        </Avatar>
                      </Box>
                      <Typography 
                        variant="h5" 
                        align="center" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 600,
                          color: '#333',
                          fontSize: '1.3rem',
                          transition: 'color 0.3s ease',
                          '&:hover': {
                            color: service.color,
                          },
                        }}
                      >
                        {service.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        align="center" 
                        sx={{ 
                          mb: 2,
                          minHeight: 40,
                          lineHeight: 1.6,
                        }}
                      >
                        {service.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Rating 
                          value={service.rating} 
                          precision={0.1} 
                          readOnly 
                          size="small"
                          sx={{ color: '#d49b7b' }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 600 }}>
                          {service.rating}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* SECTION 4: WHY CHOOSE US HEADER - With moving underline */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Fade in={true} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography 
                variant="h2" 
                component="h2" 
                sx={titleWithUnderlineStyle}
              >
                Why Choose Our Services?
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* SECTION 5: WHY CHOOSE US GRID - NO BORDER AFTER HEADER */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={4}>
          {[
            { 
              icon: <ThumbUp />, 
              title: 'Licensed & Insured', 
              desc: 'All our professionals are fully licensed, insured, and background-checked for your peace of mind.',
              gradient: 'linear-gradient(135deg, #9b4d3c 0%, #b86b5a 100%)',
            },
            { 
              icon: <Schedule />, 
              title: 'Same-Day Service', 
              desc: 'For urgent repairs, we offer same-day service to minimize disruption to your property.',
              gradient: 'linear-gradient(135deg, #d49b7b 0%, #e5b8a0 100%)',
            },
            { 
              icon: <Verified />, 
              title: 'Satisfaction Guaranteed', 
              desc: 'We stand behind our work with a 100% satisfaction guarantee on all services.',
              gradient: 'linear-gradient(135deg, #9b4d3c 0%, #d49b7b 100%)',
            },
          ].map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Zoom in={true} timeout={1000 + index * 200}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
                    borderRadius: 3,
                    border: '2px solid #9b4d3c',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 15px 30px rgba(155,77,60,0.15)',
                      borderColor: '#d49b7b',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box 
                      sx={{ 
                        background: item.gradient,
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        color: 'white',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 8px rgba(155,77,60,0.2)',
                        '&:hover': {
                          transform: 'scale(1.1) rotate(360deg)',
                          boxShadow: '0 8px 16px rgba(155,77,60,0.3)',
                        },
                      }}
                    >
                      <Box sx={{ fontSize: '1.8rem' }}>
                        {item.icon}
                      </Box>
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, fontSize: '1.3rem', color: '#333' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {item.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* SECTION 6: FOOTER - With thicker gradient top border */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Experience the Marvellous difference
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Add keyframe animation for the moving underline */}
      <style>
        {`
          @keyframes pulseWidth {
            0%, 100% { width: 80px; }
            50% { width: 120px; }
          }
        `}
      </style>
    </Box>
  );
};

export default ServicesPage;